import IntentLandingLayout from "@/components/landing/IntentLandingLayout";

export default function PermanenceTelephoniqueImmobilier() {
  return (
    <IntentLandingLayout
      canonical="/permanence-telephonique-immobilier"
      title="Permanence téléphonique immobilier pour agence | Immovox"
      description="Permanence téléphonique pour agence immobilière : assurez le relais en débordement ou hors horaires et qualifiez chaque demande avant le rappel."
      eyebrow="Permanence téléphonique immobilier"
      h1="Une permanence téléphonique immobilière quand l’agence est indisponible."
      lede="Visites, pics d’activité, fermeture ou week-end : Immovox prend le relais uniquement quand vous le décidez et restitue chaque demande à votre équipe."
      serviceType="Permanence téléphonique pour agence immobilière"
      primaryCtaLabel="Tester la permanence"
      acquisitionHero={{
        visual: "permanence",
        visualLabel: "Permanence téléphonique immobilier",
        visualTitle: "Votre agence ferme. Les appels, eux, continuent.",
        visualText: "Immovox assure le relais sur les plages que vous choisissez, sans modifier votre numéro public.",
        secondaryCtaLabel: "Voir le scénario hors horaires",
        strip: ["Vous choisissez les plages de relais", "Immovox répond pendant l’indisponibilité", "L’équipe retrouve la demande au retour"],
      }}
      definition={<>Une <strong>permanence téléphonique immobilier</strong> est un dispositif qui prend le relais lorsque l'agence ne peut pas répondre. Elle peut être assurée par une équipe humaine externalisée ou par un assistant vocal IA configuré pour accueillir, qualifier et transmettre les demandes.</>}
      sections={[
        {
          title: "Quand une agence a besoin d'une permanence téléphonique",
          bullets: [
            "Pendant les visites, estimations et rendez-vous extérieurs.",
            "Après quelques sonneries lorsque toute l'équipe est déjà occupée.",
            "Le soir, le week-end ou pendant une fermeture ponctuelle.",
            "Lors d'un pic d'appels après la publication d'une annonce.",
            "En période de congés, de sous-effectif ou de forte activité commerciale.",
          ],
        },
        {
          title: "Permanence humaine ou permanence IA : deux logiques différentes",
          paragraphs: [
            "Le télésecrétariat humain apporte une prise en charge par un opérateur et peut être pertinent lorsque l'échange nécessite une intervention humaine large ou très personnalisée.",
            "Une permanence IA vise surtout la disponibilité immédiate, la répétabilité de la qualification et la transmission d'informations structurées. Le bon choix dépend donc du type d'appels, des horaires à couvrir et du niveau de traitement attendu.",
          ],
        },
        {
          title: "Ce que la permanence doit faire avant de transmettre",
          bullets: [
            "Identifier le motif réel de l'appel au lieu de noter un message générique.",
            "Recueillir les coordonnées et le contexte nécessaires au rappel.",
            "Distinguer transaction, location, estimation, gestion et demandes administratives.",
            "Faire remonter une urgence ou une demande prioritaire selon les règles de l'agence.",
            "Restituer les informations dans un format homogène pour éviter une seconde phase de découverte.",
          ],
        },
        {
          title: "Mettre en place une permanence sans changer de numéro",
          intro: "Le scénario le plus simple repose sur un renvoi d'appel depuis la ligne existante.",
          bullets: [
            "Renvoi si personne ne décroche après un délai choisi.",
            "Renvoi uniquement en dehors des horaires d'ouverture.",
            "Renvoi temporaire pendant un événement ou une période de sous-effectif.",
            "Activation sur une ligne ou un numéro dédié lorsque l'organisation l'exige.",
          ],
        },
        {
          title: "Comment évaluer une solution de permanence téléphonique",
          bullets: [
            "Horaires réellement couverts et capacité à absorber plusieurs appels.",
            "Qualité de la qualification, pas uniquement qualité du décroché.",
            "Possibilité de personnaliser les scénarios par type d'appel.",
            "Format et rapidité de transmission des comptes rendus.",
            "Mode d'activation sur votre téléphonie actuelle.",
            "Tarification lisible et adaptée au volume réel de l'agence.",
          ],
        },
      ]}
      comparison={{
        title: "Permanence téléphonique humaine ou IA",
        headers: ["Critère", "Télésecrétariat humain", "Permanence IA Immovox"],
        rows: [
          ["Nature du service", "Opérateur qui prend l'appel", "Assistant vocal configuré pour l'agence"],
          ["Qualification", "Selon script et consignes", "Scénarios structurés par intention"],
          ["Disponibilité", "Selon les horaires du prestataire", "Selon la plage configurée par l'agence"],
          ["Restitution", "Message ou compte rendu", "Synthèse homogène après l'appel"],
          ["Usage", "Accueil externalisé", "Débordement, hors horaires ou continuité"],
        ],
      }}
      faq={[
        { question: "Quelle différence entre permanence téléphonique et standard téléphonique ?", answer: "Le standard désigne l'organisation générale des appels. La permanence est le dispositif de relais utilisé lorsque l'équipe principale n'est pas disponible." },
        { question: "Une permanence IA remplace-t-elle un télésecrétariat humain ?", answer: "Pas nécessairement. Les deux répondent à des besoins différents. L'IA est particulièrement adaptée aux scénarios répétables de prise en charge et de qualification." },
        { question: "Peut-on activer la permanence seulement après quelques sonneries ?", answer: "Oui. Un renvoi conditionnel permet de laisser d'abord l'équipe décrocher, puis d'envoyer l'appel vers Immovox si personne n'est disponible." },
        { question: "La permanence peut-elle fonctionner hors horaires ?", answer: "Oui. Les horaires de prise en charge peuvent être organisés pour couvrir les périodes où l'agence est fermée." },
        { question: "Comment comparer les prix ?", answer: "Comparez le forfait éventuel, la consommation, les plages couvertes et surtout le niveau de service réellement inclus. Les tarifs Immovox sont détaillés sur la page Tarifs." },
      ]}
      related={[
        { to: "/standard-telephonique-immobilier", label: "Standard téléphonique immobilier", description: "La solution globale de traitement des appels entrants." },
        { to: "/blog/permanence-telephonique-pour-agences-immobilieres-prix-et-services-a-comparer", label: "Prix d'une permanence téléphonique", description: "Comparer forfaits, horaires, dépassements et niveau de service." },
        { to: "/telephonie-agence-immobiliere", label: "Téléphonie d'agence", description: "Comprendre renvoi d'appel, VoIP et architecture téléphonique." },
      ]}
      ctaTitle="Mettre en place une permanence téléphonique immobilier"
      ctaText="Testez Immovox puis activez le relais uniquement sur les plages et situations qui correspondent à votre organisation."
    />
  );
}
